import React from 'react';
import styled from 'styled-components';

import Box from '@mui/material/Box';
import { SliderRail } from '@mui/material/Slider';

import { Segment } from 'ff/debrief/models';
import { flattenDebriefSegments, getScoreColor } from 'ff/debrief/utils';
import { useCurrentDebrief } from 'ff/debrief/hooks';
import { FFTheme } from 'ff/debrief/common/StyledComponentThemes';

interface Props {
    segments: Segment[];
    totalTime: number;
}

const TimelineRail: React.FC<Props> = props => {
    const { segments, totalTime, ...others } = props;
    const { isScoreModeEnabled } = useCurrentDebrief();

    const calculatePercentage = (value: number): string =>
        ((value / totalTime) * 100).toString().concat('%');

    const allSegments = flattenDebriefSegments(segments);
    const segmentList = (segments && segments[0]?.children) || [];

    const firstSegment = segmentList.at(0);

    const lastSegment = segmentList.at(-1);
    const lastSegmentEnd =
        lastSegment !== undefined
            ? lastSegment.startOffsetInSeconds + lastSegment.durationInSeconds
            : 0;
    const remainingTime = totalTime - lastSegmentEnd;

    if (segments.length === 0) {
        return (
            <SliderRail {...others}>
                <SegmentBar left={0} width={calculatePercentage(totalTime)} />
            </SliderRail>
        );
    }

    return (
        <SliderRail {...others}>
            <SegmentBar
                left={0}
                width={calculatePercentage(firstSegment.startOffsetInSeconds)}
            />
            {segmentList.map(seg => {
                const { startOffsetInSeconds, durationInSeconds } = seg;
                const segmentStart = calculatePercentage(startOffsetInSeconds);
                const segmentWidth = calculatePercentage(durationInSeconds);
                return (
                    <SegmentBar
                        key={seg.label + seg.startOffsetInSeconds}
                        id={seg.label + seg.durationInSeconds}
                        left={segmentStart}
                        width={segmentWidth}
                    />
                );
            })}
            {allSegments
                .filter(seg => typeof seg.score === 'number')
                .map(seg => {
                    const { startOffsetInSeconds, durationInSeconds, score } =
                        seg;
                    const segStart = calculatePercentage(startOffsetInSeconds);
                    const segWidth = calculatePercentage(durationInSeconds);
                    const color = isScoreModeEnabled
                        ? getScoreColor(score)
                        : FFTheme.blue;
                    return (
                        <ScoreBar
                            key={seg.label + seg.startOffsetInSeconds}
                            id={seg.label + seg.durationInSeconds}
                            left={segStart}
                            width={segWidth}
                            color={color}
                            bgcolor={color}
                        />
                    );
                })}
            <SegmentBar
                left={calculatePercentage(lastSegmentEnd)}
                width={calculatePercentage(remainingTime)}
            />
        </SliderRail>
    );
};

const BaseBar = styled(Box)`
    position: absolute;
    height: 100%;
    box-sizing: border-box;
    border-radius: 2px;
`;

const SegmentBar = styled(BaseBar)`
    background-color: #293e52;
    border-left: 1px solid #ffffff;
    border-right: 1px solid #ffffff;
`;

const ScoreBar = styled(BaseBar)`
    border-left: 1px solid;
    border-right: 1px solid;
`;

export default TimelineRail;
